import React, { useEffect, useState, createElement } from "react";
import {
  MenuItem,
  TextField,
  TextFieldProps,
  SxProps,
  Grid,
} from "@mui/material";
import { Control, Controller, ControllerProps } from "react-hook-form";
import CustomTooltip from "./customTooltip";

export type SelectElementProps = Omit<
  TextFieldProps,
  "name" | "type" | "onChange"
> & {
  validation?: ControllerProps["rules"];
  name: string;
  toolTipTitle?: string;
  options?: any[];
  valueKey?: string;
  labelKey?: string;
  type?: "string" | "number";
  sxProps?: SxProps;
  objectOnChange?: boolean;
  selectedIndex?: number;
  onItemChange?: (value: any) => void;
  control?: Control<any>;
  label?: string;
};

export default function SelectElement({
  name,
  toolTipTitle,
  required,
  valueKey = "id",
  labelKey = "title",
  options = [],
  sxProps,
  type,
  selectedIndex,
  onItemChange,
  objectOnChange,
  validation = {},
  control,
  label,
  ...rest
}: SelectElementProps): JSX.Element {
  const isNativeSelect = !!rest.SelectProps?.native;
  const ChildComponent = isNativeSelect ? "option" : MenuItem;

  const [defaultValue, setDefaultValue] = useState("");

  useEffect(() => {
    if (
      selectedIndex === undefined ||
      selectedIndex === null ||
      selectedIndex <= -1
    ) {
      return;
    }

    if (options === undefined || options === null) {
      return;
    }

    if (selectedIndex > options.length - 1) {
      return;
    }

    let selected = options[selectedIndex][valueKey];
    setDefaultValue(selected);

    if (typeof onItemChange === "function") {
      let item = options.find((i) => i[valueKey] === selected);
      onItemChange(item);
    }
  }, [options.length]);

  return (
    <Controller
      name={name}
      rules={validation}
      control={control}
      render={({
        field: { onBlur, onChange, value },
        fieldState: { error },
      }) => {
        // handle shrink on number input fields
        if (type === "number" && value) {
          rest.InputLabelProps = rest.InputLabelProps || {};
          rest.InputLabelProps.shrink = true;
        }
        if (typeof value === "object") {
          value = value[valueKey]; // if value is object get key
        }
        return (
          <Grid container alignItems="center">
            <Grid item>
              <Grid container alignItems="center">
                <Grid item>
                  <Grid container alignItems="center">
                    <Grid
                      item
                      sx={{
                        fontSize: "14px",
                        fontFamily: "Poppins",
                        fontWeight: "300",
                        color: "#A5A5A5",
                        mb: "4px",
                        ml: "4px",
                        mr: "6px",
                      }}
                    >
                      {label}
                    </Grid>
                    {toolTipTitle && <CustomTooltip title={toolTipTitle} />}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <TextField
              {...rest}
              size="small"
              name={name}
              value={value || defaultValue || ""}
              onBlur={onBlur}
              onChange={(event) => {
                let item: number | string = event.target.value;
                if (type === "number") {
                  item = Number(item);
                }
                onChange(item);
                if (typeof onItemChange === "function") {
                  if (objectOnChange) {
                    item = options.find((i) => i[valueKey] === item);
                  }
                  onItemChange(item);
                }
              }}
              sx={{
                borderRadius: "100px",
                color: "#000000",
                "& .MuiInputLabel-root": {
                  color: "#cccccc", // Label color
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "100px",
                  "& fieldset": {
                    borderRadius: "100px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#cccccc", // Hovered border color
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#cccccc", // Focused border color
                  },
                  "& input": {
                    color: "#000000", // Text color inside input
                  },
                  "& input::placeholder": {
                    color: "#000000", // Placeholder text color
                   // opacity: 1,
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#A5A5A5", // Dropdown icon color
                  },
                  "& .MuiSelect-select": {
                    color: "#000000", // Selected value color
                  },
                  "& .MuiMenuItem-root": {
                    color: "#000000", // Option color
                  },
                  "& select": {
                    color: "#000000", // Native select color
                  },
                },
                "& .MuiInputBase-input": {
                  color: "#000000", // Base input text color
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#000000", // Placeholder text color
                 // opacity: 1,
                },
              }}
              select
              required={required}
              error={!!error}
              helperText={error ? error.message : rest?.helperText}
            >
              {isNativeSelect && <option />}
              {options.map((item: any) =>
                createElement(
                  ChildComponent,
                  {
                    key: `${name}_${item[valueKey]}`,
                    value: item[valueKey],
                  },
                  item[labelKey]
                )
              )}
            </TextField>
          </Grid>
        );
      }}
    />
  );
}
